import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { toast } from 'sonner';
import {
    Stamp, Loader2, ShieldCheck, FileText, Upload, Send, CheckCircle2, Clock, AlertTriangle,
    Euro, TrendingUp, FileBadge, UserCheck, Download, Mail, Phone, Building2, ChevronRight, Search,
} from 'lucide-react';

const STATO_COLORS = {
    pending: 'bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]',
    pagata: 'bg-[hsl(210_90%_94%)] text-[hsl(210_90%_40%)]',
    in_lavorazione: 'bg-[hsl(45_100%_92%)] text-[hsl(35_90%_35%)]',
    completata: 'bg-[hsl(145_55%_34%)] text-white',
    scaduta: 'bg-[hsl(0_80%_95%)] text-[hsl(0_72%_52%)]',
};
const COMPL_COLORS = {
    easy:    'bg-[hsl(145_45%_94%)] text-[hsl(145_55%_34%)]',
    medium:  'bg-[hsl(45_100%_92%)] text-[hsl(35_90%_35%)]',
    complex: 'bg-[hsl(0_80%_95%)] text-[hsl(0_72%_52%)]',
};
const URGENZA_LABELS = {
    standard: { icon: Clock, color: 'text-[hsl(var(--muted-foreground))]' },
    veloce:   { icon: TrendingUp, color: 'text-[hsl(35_90%_45%)]' },
    urgente:  { icon: AlertTriangle, color: 'text-[hsl(0_72%_52%)]' },
};

export default function AdminFirma() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const isAdmin = user && (user.role === 'admin' || ['demo@cantiere.ai', 'alessandroscarpuzza@gmail.com'].includes((user.email || '').toLowerCase()));

    const [stats, setStats] = useState(null);
    const [items, setItems] = useState([]);
    const [pros, setPros] = useState([]);
    const [filter, setFilter] = useState('all');
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);

    const [assignOpen, setAssignOpen] = useState(false);
    const [assignProId, setAssignProId] = useState('');
    const [assignNote, setAssignNote] = useState('');
    const [assignSaving, setAssignSaving] = useState(false);

    const [uploadingPdf, setUploadingPdf] = useState(false);
    const [delivering, setDelivering] = useState(false);

    const loadAll = async () => {
        setLoading(true);
        try {
            const [s, l, p] = await Promise.all([
                api.get('/admin/firma/stats'),
                api.get(`/admin/firma/list${filter !== 'all' ? `?stato=${filter}` : ''}`),
                api.get('/admin/professionisti'),
            ]);
            setStats(s.data);
            setItems(l.data);
            setPros(p.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || t('common.generic_error'));
        } finally { setLoading(false); }
    };

    useEffect(() => { if (isAdmin) loadAll(); /* eslint-disable-next-line */ }, [isAdmin, filter, refreshKey]);

    const filteredItems = useMemo(() => {
        if (!search) return items;
        const s = search.toLowerCase();
        return items.filter((i) =>
            (i.dvr_snapshot?.tipologia_lavoro || '').toLowerCase().includes(s) ||
            (i.impresa?.company_name || '').toLowerCase().includes(s) ||
            (i.impresa?.email || '').toLowerCase().includes(s) ||
            (i.contatto_email || '').toLowerCase().includes(s)
        );
    }, [items, search]);

    if (!isAdmin) {
        return (
            <div className="p-10 text-center text-[hsl(var(--muted-foreground))]" data-testid="admin-only">
                <ShieldCheck className="w-12 h-12 mx-auto mb-3" />
                <p className="text-lg font-semibold">{t('admin.admin_only', 'Accesso riservato agli amministratori')}</p>
            </div>
        );
    }

    const openDetail = async (id) => {
        try {
            const r = await api.get(`/admin/firma/${id}`);
            setDetail(r.data);
            setAssignProId(r.data.assigned_professionista_id || '');
            setAssignNote('');
        } catch (e) {
            toast.error(e.response?.data?.detail || t('common.generic_error'));
        }
    };

    const closeDetail = () => { setDetail(null); setAssignOpen(false); };

    const downloadDvr = () => {
        if (!detail?.dvr) return;
        const content = `BOZZA TECNICA DVR — D.Lgs 81/2008
Generata da AI, non valida legalmente senza firma professionale

=============================================
TIPOLOGIA LAVORO: ${detail.dvr.tipologia_lavoro}
DESCRIZIONE: ${detail.dvr.descrizione}
=============================================

RISCHI IDENTIFICATI:
${(detail.dvr.valutazione?.rischi || []).map((r, i) => `
${i + 1}. ${r.rischio} [LIVELLO: ${r.livello?.toUpperCase()}]
   Probabilità: ${r.probabilita} | Gravità: ${r.gravita}
   Misure di prevenzione:
${(r.misure_prevenzione || []).map((m) => `     - ${m}`).join('\n')}
   DPI richiesti:
${(r.dpi_richiesti || []).map((d) => `     - ${d}`).join('\n')}
`).join('\n')}

FORMAZIONE RICHIESTA:
${(detail.dvr.valutazione?.formazione_richiesta || []).map((f) => `  - ${f}`).join('\n')}

DOCUMENTI OBBLIGATORI:
${(detail.dvr.valutazione?.documenti_obbligatori || []).map((d) => `  - ${d}`).join('\n')}

NOTE: ${detail.dvr.valutazione?.note || ''}

=============================================
Pratica firma ID: ${detail.id}
Cliente: ${detail.impresa?.company_name || detail.contatto_email}
Generato: ${detail.created_at}
`;
        const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `BOZZA_DVR_${detail.id.slice(0, 8)}.txt`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const doAssign = async () => {
        if (!assignProId) { toast.error(t('pages.adm_fi_select_pro', 'Seleziona un professionista')); return; }
        setAssignSaving(true);
        try {
            await api.post(`/admin/firma/${detail.id}/assign`, { professionista_id: assignProId, note: assignNote });
            toast.success(t('pages.adm_fi_assigned', 'Professionista assegnato + email inviata'));
            setAssignOpen(false);
            await openDetail(detail.id);
            setRefreshKey((k) => k + 1);
        } catch (e) {
            toast.error(e.response?.data?.detail || t('common.generic_error'));
        } finally { setAssignSaving(false); }
    };

    const onPdfChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 20 * 1024 * 1024) { toast.error(t('pages.adm_fi_pdf_too_big', 'PDF troppo grande (max 20MB)')); return; }
        setUploadingPdf(true);
        try {
            const fd = new FormData();
            fd.append('pdf', file);
            await api.post(`/admin/firma/${detail.id}/upload-pdf`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            toast.success(t('pages.adm_fi_pdf_uploaded', 'PDF firmato caricato'));
            await openDetail(detail.id);
            setRefreshKey((k) => k + 1);
        } catch (e) {
            toast.error(e.response?.data?.detail || t('common.generic_error'));
        } finally {
            setUploadingPdf(false);
            e.target.value = '';
        }
    };

    const downloadSignedPdf = async () => {
        try {
            const r = await api.get(`/admin/firma/${detail.id}/pdf`, { responseType: 'blob' });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = `DVR_firmato_${detail.id.slice(0, 8)}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(e.response?.data?.detail || t('common.generic_error'));
        }
    };

    const doDeliver = async () => {
        if (!window.confirm(t('pages.adm_fi_confirm_deliver', 'Inviare il PDF firmato al cliente?'))) return;
        setDelivering(true);
        try {
            const r = await api.post(`/admin/firma/${detail.id}/deliver`);
            toast.success(t('pages.adm_fi_delivered', { email: r.data.delivered_to }) || `Inviato a ${r.data.delivered_to}`);
            await openDetail(detail.id);
            setRefreshKey((k) => k + 1);
        } catch (e) {
            toast.error(e.response?.data?.detail || t('common.generic_error'));
        } finally { setDelivering(false); }
    };

    const KPI = ({ icon: Icon, label, value, color = 'text-[hsl(var(--foreground))]', testid }) => (
        <Card className="p-4" data-testid={testid}>
            <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg bg-[hsl(var(--muted))]/40 ${color}`}><Icon className="w-5 h-5" /></div>
                <div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">{label}</div>
                    <div className="font-display text-2xl tabular-nums">{value}</div>
                </div>
            </div>
        </Card>
    );

    return (
        <div data-testid="admin-firma-page">
            <div className="mb-6">
                <h1 className="font-display text-4xl md:text-5xl flex items-center gap-3">
                    <Stamp className="w-9 h-9 text-[hsl(var(--accent))]" />
                    {t('pages.adm_fi_title', 'Firma Professionale · Admin')}
                </h1>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('pages.adm_fi_subtitle', 'Gestione richieste di firma DVR (D.Lgs 81/2008)')}</p>
            </div>

            {/* KPI Dashboard */}
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
                    <KPI icon={FileBadge} label={t('pages.adm_fi_kpi_total', 'Totale')} value={stats.total} testid="kpi-total" />
                    <KPI icon={Clock} label={t('pages.adm_fi_kpi_pagate', 'Da assegnare')} value={stats.pagate} color="text-[hsl(210_90%_40%)]" testid="kpi-pagate" />
                    <KPI icon={UserCheck} label={t('pages.adm_fi_kpi_in_lavorazione', 'In lavorazione')} value={stats.in_lavorazione} color="text-[hsl(35_90%_45%)]" testid="kpi-in-lavorazione" />
                    <KPI icon={CheckCircle2} label={t('pages.adm_fi_kpi_completate', 'Completate')} value={stats.completate} color="text-[hsl(145_55%_34%)]" testid="kpi-completate" />
                    <KPI icon={AlertTriangle} label={t('pages.adm_fi_kpi_scadute', 'Scadute')} value={stats.scadute} color="text-[hsl(0_72%_52%)]" testid="kpi-scadute" />
                    <KPI icon={Euro} label={t('pages.adm_fi_kpi_rev_month', 'Fatturato mese')} value={`€${Number(stats.revenue_month).toLocaleString('it-IT', { minimumFractionDigits: 2 })}`} color="text-[hsl(var(--accent))]" testid="kpi-rev-month" />
                    <KPI icon={TrendingUp} label={t('pages.adm_fi_kpi_rev_total', 'Tot. fatturato')} value={`€${Number(stats.revenue_all_time).toLocaleString('it-IT', { minimumFractionDigits: 2 })}`} testid="kpi-rev-total" />
                </div>
            )}

            {/* Filters & search */}
            <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
                <Tabs value={filter} onValueChange={setFilter} className="flex-1">
                    <TabsList className="w-full md:w-auto flex flex-wrap h-auto">
                        <TabsTrigger value="all" data-testid="tab-all">{t('pages.adm_fi_tab_all', 'Tutte')}</TabsTrigger>
                        <TabsTrigger value="pagata" data-testid="tab-pagata">{t('pages.adm_fi_tab_pagate', 'Da assegnare')}{stats?.pagate ? ` (${stats.pagate})` : ''}</TabsTrigger>
                        <TabsTrigger value="in_lavorazione" data-testid="tab-in-lavorazione">{t('pages.adm_fi_tab_in_lav', 'In lavorazione')}{stats?.in_lavorazione ? ` (${stats.in_lavorazione})` : ''}</TabsTrigger>
                        <TabsTrigger value="completata" data-testid="tab-completata">{t('pages.adm_fi_tab_completate', 'Completate')}</TabsTrigger>
                        <TabsTrigger value="scaduta" data-testid="tab-scaduta">{t('pages.adm_fi_tab_scadute', 'Scadute')}{stats?.scadute ? ` (${stats.scadute})` : ''}</TabsTrigger>
                    </TabsList>
                </Tabs>
                <div className="relative md:w-72">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t('pages.adm_fi_search_ph', 'Cerca cliente, lavoro...')}
                        className="w-full pl-9 pr-3 h-10 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent))]"
                        data-testid="admin-fi-search"
                    />
                </div>
            </div>

            {/* List */}
            {loading ? (
                <div className="text-center py-10"><Loader2 className="w-8 h-8 mx-auto animate-spin text-[hsl(var(--accent))]" /></div>
            ) : filteredItems.length === 0 ? (
                <Card className="p-10 text-center text-[hsl(var(--muted-foreground))]" data-testid="admin-fi-empty">
                    <Stamp className="w-12 h-12 mx-auto mb-3" />
                    <p>{t('pages.adm_fi_empty', 'Nessuna richiesta per questo filtro')}</p>
                </Card>
            ) : (
                <Card className="p-0 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-[hsl(var(--muted))]/40 border-b border-[hsl(var(--border))]">
                                <tr className="text-left text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                                    <th className="p-3">{t('pages.adm_fi_col_client', 'Cliente')}</th>
                                    <th className="p-3">{t('pages.adm_fi_col_work', 'Lavoro')}</th>
                                    <th className="p-3">{t('pages.adm_fi_col_compl', 'Complessità')}</th>
                                    <th className="p-3">{t('pages.adm_fi_col_urg', 'Urgenza')}</th>
                                    <th className="p-3 text-right">{t('pages.adm_fi_col_amount', 'Importo')}</th>
                                    <th className="p-3">{t('pages.adm_fi_col_pro', 'Professionista')}</th>
                                    <th className="p-3">{t('pages.adm_fi_col_status', 'Stato')}</th>
                                    <th className="p-3"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredItems.map((it) => {
                                    const UrgIcon = (URGENZA_LABELS[it.urgenza] || URGENZA_LABELS.standard).icon;
                                    const urgColor = (URGENZA_LABELS[it.urgenza] || URGENZA_LABELS.standard).color;
                                    return (
                                        <tr key={it.id} className="border-b border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]/30 cursor-pointer" onClick={() => openDetail(it.id)} data-testid={`admin-fi-row-${it.id}`}>
                                            <td className="p-3">
                                                <div className="font-semibold">{it.impresa?.company_name || '-'}</div>
                                                <div className="text-xs text-[hsl(var(--muted-foreground))]">{it.contatto_email || it.impresa?.email}</div>
                                            </td>
                                            <td className="p-3 max-w-[220px]">
                                                <div className="truncate font-medium">{it.dvr_snapshot?.tipologia_lavoro || '-'}</div>
                                                <div className="text-xs text-[hsl(var(--muted-foreground))] truncate">{it.descrizione_lavoro}</div>
                                            </td>
                                            <td className="p-3"><Badge className={`${COMPL_COLORS[it.complessita] || ''} text-xs`}>{it.complessita}</Badge></td>
                                            <td className="p-3"><div className={`flex items-center gap-1 text-xs ${urgColor}`}><UrgIcon className="w-3.5 h-3.5" /> {it.urgenza}</div></td>
                                            <td className="p-3 text-right font-semibold tabular-nums">€{Number(it.prezzo_totale).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</td>
                                            <td className="p-3">
                                                {it.professionista ? (
                                                    <div className="text-xs">
                                                        <div className="font-semibold">{it.professionista.nome}</div>
                                                        <div className="text-[hsl(var(--muted-foreground))]">{it.professionista.qualifica}</div>
                                                    </div>
                                                ) : (
                                                    <Badge variant="outline" className="text-xs">{t('pages.adm_fi_unassigned', 'Da assegnare')}</Badge>
                                                )}
                                            </td>
                                            <td className="p-3"><Badge className={`${STATO_COLORS[it.scaduta ? 'scaduta' : it.stato] || ''} text-xs`}>{it.scaduta ? (t('pages.adm_fi_st_scaduta', 'scaduta')) : (t(`pages.adm_fi_st_${it.stato}`, it.stato))}</Badge></td>
                                            <td className="p-3 text-[hsl(var(--muted-foreground))]"><ChevronRight className="w-4 h-4" /></td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </Card>
            )}

            {/* Detail Dialog */}
            <Dialog open={!!detail} onOpenChange={(o) => { if (!o) closeDetail(); }}>
                <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="admin-fi-detail-dialog">
                    {detail && (
                        <div>
                            <DialogHeader>
                                <DialogTitle className="font-display text-2xl flex items-center gap-2 flex-wrap">
                                    <Stamp className="w-6 h-6 text-[hsl(var(--accent))]" />
                                    {detail.dvr_snapshot?.tipologia_lavoro || 'DVR'}
                                    <Badge className={`${STATO_COLORS[detail.scaduta ? 'scaduta' : detail.stato]} text-xs ml-2`}>
                                        {detail.scaduta ? (t('pages.adm_fi_st_scaduta', 'scaduta')) : (t(`pages.adm_fi_st_${detail.stato}`, detail.stato))}
                                    </Badge>
                                </DialogTitle>
                            </DialogHeader>

                            {/* Client + Job grid */}
                            <div className="grid md:grid-cols-2 gap-4 mt-3">
                                <Card className="p-4">
                                    <div className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))] mb-2 flex items-center gap-1"><Building2 className="w-3 h-3" /> {t('pages.adm_fi_client', 'Cliente')}</div>
                                    <div className="font-semibold">{detail.impresa?.company_name || '-'}</div>
                                    <div className="text-sm text-[hsl(var(--muted-foreground))]">P.IVA: {detail.impresa?.piva || '-'}</div>
                                    <div className="text-sm flex items-center gap-1 mt-1"><Mail className="w-3.5 h-3.5" /> {detail.contatto_email || detail.impresa?.email}</div>
                                    {detail.contatto_telefono && <div className="text-sm flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {detail.contatto_telefono}</div>}
                                </Card>
                                <Card className="p-4">
                                    <div className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))] mb-2 flex items-center gap-1"><FileText className="w-3 h-3" /> {t('pages.adm_fi_job', 'Cantiere')}</div>
                                    <div className="text-sm space-y-0.5">
                                        <div><strong>{t('pages.adm_fi_workers', 'Lavoratori')}:</strong> {detail.n_lavoratori || '-'}</div>
                                        <div><strong>{t('pages.adm_fi_days', 'Durata')}:</strong> {detail.durata_giorni ? `${detail.durata_giorni} gg` : '-'}</div>
                                        <div><strong>{t('pages.adm_fi_height', 'Altezza max')}:</strong> {detail.altezza_metri ? `${detail.altezza_metri} m` : '-'}</div>
                                        <div className="flex items-center gap-2 mt-2">
                                            <Badge className={`${COMPL_COLORS[detail.complessita]} text-xs`}>{detail.complessita}</Badge>
                                            <Badge variant="outline" className="text-xs">{detail.urgenza}</Badge>
                                            <span className="font-semibold tabular-nums">€{Number(detail.prezzo_totale).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</span>
                                        </div>
                                    </div>
                                </Card>
                            </div>

                            {/* Customer description */}
                            <div className="mt-4">
                                <Label className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">{t('pages.adm_fi_client_desc', 'Descrizione fornita dal cliente')}</Label>
                                <p className="text-sm mt-1 p-3 rounded-lg bg-[hsl(var(--muted))]/30">{detail.descrizione_lavoro}</p>
                            </div>

                            {/* DVR Draft */}
                            <Card className="p-4 mt-4 bg-[hsl(45_100%_97%)] border-[hsl(45_100%_60%)]">
                                <div className="flex items-center justify-between flex-wrap gap-2">
                                    <div>
                                        <div className="font-semibold flex items-center gap-2"><FileText className="w-4 h-4" /> {t('pages.adm_fi_dvr_draft', 'Bozza AI')} · DVR</div>
                                        <div className="text-xs text-[hsl(var(--muted-foreground))]">{(detail.dvr?.valutazione?.rischi || []).length} {t('pages.adm_fi_dvr_risks', 'rischi')} · {(detail.dvr?.valutazione?.documenti_obbligatori || []).length} {t('pages.adm_fi_dvr_docs', 'doc obbligatori')}</div>
                                    </div>
                                    <Button onClick={downloadDvr} variant="outline" size="sm" className="gap-2" data-testid="admin-fi-download-draft">
                                        <Download className="w-3.5 h-3.5" /> {t('pages.adm_fi_dl_draft', 'Scarica bozza')}
                                    </Button>
                                </div>
                            </Card>

                            {/* Workflow actions */}
                            <div className="mt-5 space-y-3">
                                {/* 1. Assign professional */}
                                <Card className={`p-4 border-2 ${detail.assigned_professionista_id ? 'border-[hsl(145_55%_70%)]' : 'border-[hsl(45_100%_60%)] bg-[hsl(45_100%_97%)]'}`}>
                                    <div className="flex items-center justify-between flex-wrap gap-2">
                                        <div className="flex items-center gap-2">
                                            <div className={`p-2 rounded-full ${detail.assigned_professionista_id ? 'bg-[hsl(145_55%_34%)] text-white' : 'bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)]'}`}>
                                                {detail.assigned_professionista_id ? <CheckCircle2 className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                                            </div>
                                            <div>
                                                <div className="font-semibold">{t('pages.adm_fi_step1', '1. Assegna professionista')}</div>
                                                {detail.professionista ? (
                                                    <div className="text-xs text-[hsl(var(--muted-foreground))]">{detail.professionista.nome} · {detail.professionista.qualifica} · {detail.professionista.email}</div>
                                                ) : (
                                                    <div className="text-xs text-[hsl(var(--muted-foreground))]">{t('pages.adm_fi_step1_hint', 'Seleziona un RSPP/CSP/CSE dal network')}</div>
                                                )}
                                            </div>
                                        </div>
                                        <Button onClick={() => setAssignOpen(true)} size="sm" className="bg-[hsl(220_13%_10%)] hover:bg-[hsl(220_13%_18%)] text-white gap-2" data-testid="admin-fi-assign-btn">
                                            {detail.assigned_professionista_id ? (t('pages.adm_fi_reassign', 'Riassegna')) : (t('pages.adm_fi_assign', 'Assegna'))}
                                        </Button>
                                    </div>
                                </Card>

                                {/* 2. Upload PDF */}
                                <Card className={`p-4 border-2 ${detail.has_pdf ? 'border-[hsl(145_55%_70%)]' : detail.assigned_professionista_id ? 'border-[hsl(45_100%_60%)] bg-[hsl(45_100%_97%)]' : 'border-[hsl(var(--border))] opacity-60'}`}>
                                    <div className="flex items-center justify-between flex-wrap gap-2">
                                        <div className="flex items-center gap-2">
                                            <div className={`p-2 rounded-full ${detail.has_pdf ? 'bg-[hsl(145_55%_34%)] text-white' : 'bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]'}`}>
                                                {detail.has_pdf ? <CheckCircle2 className="w-4 h-4" /> : <Upload className="w-4 h-4" />}
                                            </div>
                                            <div>
                                                <div className="font-semibold">{t('pages.adm_fi_step2', '2. Carica PDF firmato')}</div>
                                                <div className="text-xs text-[hsl(var(--muted-foreground))]">
                                                    {detail.has_pdf
                                                        ? `${t('pages.adm_fi_pdf_ready', 'PDF caricato')} · ${Math.round((detail.pdf_size || 0) / 1024)} KB`
                                                        : (t('pages.adm_fi_step2_hint', 'PDF max 20MB, firmato e timbrato dal professionista'))}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            {detail.has_pdf && (
                                                <Button onClick={downloadSignedPdf} size="sm" variant="outline" className="gap-2" data-testid="admin-fi-dl-signed">
                                                    <Download className="w-3.5 h-3.5" /> {t('common.download', 'Scarica')}
                                                </Button>
                                            )}
                                            <label className={`inline-flex items-center gap-2 cursor-pointer px-3 h-9 rounded-md text-sm font-medium ${detail.assigned_professionista_id ? 'bg-[hsl(220_13%_10%)] hover:bg-[hsl(220_13%_18%)] text-white' : 'bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] cursor-not-allowed'}`}>
                                                {uploadingPdf ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                                                {detail.has_pdf ? (t('pages.adm_fi_replace_pdf', 'Sostituisci')) : (t('pages.adm_fi_upload_pdf', 'Carica PDF'))}
                                                <input type="file" accept="application/pdf" onChange={onPdfChange} disabled={!detail.assigned_professionista_id || uploadingPdf} className="hidden" data-testid="admin-fi-pdf-input" />
                                            </label>
                                        </div>
                                    </div>
                                </Card>

                                {/* 3. Deliver */}
                                <Card className={`p-4 border-2 ${detail.stato === 'completata' ? 'border-[hsl(145_55%_70%)] bg-[hsl(145_45%_97%)]' : detail.has_pdf ? 'border-[hsl(45_100%_60%)] bg-[hsl(45_100%_97%)]' : 'border-[hsl(var(--border))] opacity-60'}`}>
                                    <div className="flex items-center justify-between flex-wrap gap-2">
                                        <div className="flex items-center gap-2">
                                            <div className={`p-2 rounded-full ${detail.stato === 'completata' ? 'bg-[hsl(145_55%_34%)] text-white' : 'bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]'}`}>
                                                {detail.stato === 'completata' ? <CheckCircle2 className="w-4 h-4" /> : <Send className="w-4 h-4" />}
                                            </div>
                                            <div>
                                                <div className="font-semibold">{t('pages.adm_fi_step3', '3. Invia al cliente')}</div>
                                                <div className="text-xs text-[hsl(var(--muted-foreground))]">
                                                    {detail.stato === 'completata' && detail.delivered_at
                                                        ? `${t('pages.adm_fi_delivered_at', 'Inviato il')} ${new Date(detail.delivered_at).toLocaleString('it-IT')} · ${detail.delivered_to}`
                                                        : (t('pages.adm_fi_step3_hint', 'Email automatica al cliente con PDF firmato in allegato'))}
                                                </div>
                                            </div>
                                        </div>
                                        {detail.stato !== 'completata' && (
                                            <Button onClick={doDeliver} disabled={!detail.has_pdf || delivering} size="sm" className="bg-[hsl(145_55%_34%)] hover:bg-[hsl(145_55%_28%)] text-white gap-2" data-testid="admin-fi-deliver-btn">
                                                {delivering ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                                                {t('pages.adm_fi_deliver', 'Invia ora')}
                                            </Button>
                                        )}
                                        {detail.stato === 'completata' && detail.delivered_email_id && (
                                            <Badge className="bg-[hsl(145_55%_34%)] text-white gap-1"><Mail className="w-3 h-3" /> {detail.delivered_email_id.slice(0, 12)}...</Badge>
                                        )}
                                    </div>
                                </Card>
                            </div>

                            {detail.admin_note && (
                                <Alert className="mt-4">
                                    <AlertDescription className="text-xs whitespace-pre-wrap">{detail.admin_note}</AlertDescription>
                                </Alert>
                            )}

                            <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-4 text-center">
                                ID: {detail.id} · {t('pages.adm_fi_paid_at', 'Pagato')}: {detail.paid_at ? new Date(detail.paid_at).toLocaleString('it-IT') : '-'}
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Assign Dialog */}
            <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
                <DialogContent className="max-w-md" data-testid="admin-fi-assign-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display text-xl">{t('pages.adm_fi_assign_title', 'Assegna professionista')}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-2">
                        <div>
                            <Label>{t('pages.adm_fi_select_pro_label', 'Professionista')} <span className="text-[hsl(var(--destructive))]">*</span></Label>
                            <Select value={assignProId} onValueChange={setAssignProId}>
                                <SelectTrigger className="mt-1" data-testid="admin-fi-pro-select"><SelectValue placeholder={t('pages.adm_fi_select_pro_ph', 'Seleziona...')} /></SelectTrigger>
                                <SelectContent>
                                    {pros.filter((p) => p.attivo !== false).map((p) => (
                                        <SelectItem key={p.id} value={p.id}>{p.nome} — {p.qualifica} · €{p.costo_per_pratica}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {pros.length === 0 && (
                                <p className="text-xs text-[hsl(0_72%_52%)] mt-1">
                                    {t('pages.adm_fi_no_pros', 'Nessun professionista configurato. Aggiungine almeno uno nella sezione Network.')}
                                </p>
                            )}
                        </div>
                        <div>
                            <Label>{t('pages.adm_fi_assign_note', 'Note per il professionista (opzionale)')}</Label>
                            <Textarea rows={3} value={assignNote} onChange={(e) => setAssignNote(e.target.value)} placeholder={t('pages.adm_fi_assign_note_ph', 'Es: richiede sopralluogo urgente, cliente disponibile lun-ven 9-18')} className="mt-1" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAssignOpen(false)}>{t('common.cancel', 'Annulla')}</Button>
                        <Button onClick={doAssign} disabled={assignSaving || !assignProId} className="bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] font-bold gap-2" data-testid="admin-fi-assign-save">
                            {assignSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                            {t('pages.adm_fi_assign_send', 'Assegna e notifica')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
